
import React, { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { User, NewUserData, UpdateUserData } from '../types';
import { API_BASE_URL, forceHttpsUrl } from '../constants';


interface UserContextType {
  users: User[];
  loading: boolean;
  addUser: (userData: NewUserData) => Promise<void>;
  updateUser: (userId: string, userData: UpdateUserData) => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
}

export const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);

    // Função de ordenação definitiva: limpa espaços e ignora acentos/caso
    const sortUsersAlphabetically = (userList: User[]) => {
        return [...userList].sort((a, b) => {
            const nameA = (a.name || '').trim();
            const nameB = (b.name || '').trim();
            return nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base' });
        });
    };

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/users`);
            if (!response.ok) throw new Error('Failed to fetch users');
            const data: User[] = await response.json();
            const httpsData = data.map(user => ({
                ...user,
                avatarUrl: forceHttpsUrl(user.avatarUrl),
            }));
            setUsers(sortUsersAlphabetically(httpsData));
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);
    
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);


    const addUser = useCallback(async (userData: NewUserData) => {
        const newUser: User = {
            id: `user_${Date.now()}`,
            password: userData.password || '123',
            ...userData,
        };
        try {
            const response = await fetch(`${API_BASE_URL}/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newUser),
            });
            if (!response.ok) throw new Error('Failed to add user');
            const addedUser = await response.json();
            setUsers(current => sortUsersAlphabetically([...current, addedUser]));
        } catch (error) {
            console.error(error);
        }
    }, []);

    const updateUser = useCallback(async (userId: string, userData: UpdateUserData) => {
        if (userData.password === '') {
            delete userData.password;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(userData),
            });
            if (!response.ok) throw new Error('Failed to update user');
            const updatedUser = await response.json();
            setUsers(current => sortUsersAlphabetically(current.map(u => u.id === userId ? updatedUser : u)));
        } catch (error) {
            console.error(error);
        }
    }, []);

    const deleteUser = useCallback(async (userId: string) => {
        if (userId === 'admin001' || userId === 'dev001') {
            alert('Não é possível excluir os usuários principais do sistema.');
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Failed to delete user');
            setUsers(current => current.filter(user => user.id !== userId));
        } catch (error) {
            console.error(error);
        }
    }, []);

    return (
        <UserContext.Provider value={{ users, loading, addUser, updateUser, deleteUser }}>
            {children}
        </UserContext.Provider>
    );
};
